// Request/response contract for POST /api/agent-research/search on the
// ag-reports-converter service. Mirrors the server-side zod schemas.

export type AgentResearchField = 'name' | 'address' | 'email' | 'phone' | 'agent_number' | 'agent_id' | 'upline';
export type AgentResearchSource = 'registry' | 'downline';

export interface AgentResearchSearchBody {
  term: string;
  fields?: AgentResearchField[]; // omit to auto-detect (name+email / phone+id / email)
  threshold?: number; // 0..1, fuzzy cut-off (default 0.4)
  limit?: number; // default 20
  sources?: AgentResearchSource[]; // default ['registry','downline']
}

export interface AgentResearchFact {
  kind: string;
  values: string[];
}

export interface AgentResearchDetails {
  recordType: AgentResearchSource;
  npn: string | null;
  entityKind: string | null; // person | org (downline)
  agentStatus: string | null; // registry status
  firstSeen: string | null;
  lastSeen: string | null;
  seenInFiles: number | null;
  /**
   * Downline only. true = in the carrier's current downline file; false = only in
   * older files, i.e. out of production for a while; null = the carrier has no
   * active file to judge against, or this is a registry record.
   */
  inActiveFile: boolean | null;
  /** Report date of the active file the flag was judged against. */
  activeReportDate: string | null;
  facts: AgentResearchFact[];
}

export interface AgentResearchMatch {
  source: string; // 'registry' | 'downline_nlg' | ...
  matchMode: 'exact' | 'fuzzy';
  score: number; // 0..1
  scorePercent: number; // score * 100
  recordType: AgentResearchSource;
  recordId: number;
  agentId: number | null; // internal-id verdict; null = not resolved
  firebaseDbId: string | null;
  displayName: string | null;
  matchedKind: string;
  matchedValue: string | null;
  details: AgentResearchDetails | null;
}

export type AgentResearchVerdict = 'agent_id_found' | 'candidates_only' | 'no_match';

export interface AgentResearchSearchResponse {
  term: string;
  fields: AgentResearchField[];
  verdict: AgentResearchVerdict;
  agentIdFound: number | null;
  matches: AgentResearchMatch[];
}

export interface AgentResearchFieldOption {
  value: AgentResearchField;
  label: string;
  hint: string;
}

// Fields offered in the UI (single-select — the user declares what the term is).
export const AGENT_RESEARCH_FIELD_OPTIONS: AgentResearchFieldOption[] = [
  { value: 'name', label: 'Name', hint: 'fuzzy' },
  { value: 'email', label: 'Email', hint: 'exact + fuzzy' },
  { value: 'phone', label: 'Phone', hint: 'exact' },
  { value: 'address', label: 'Address', hint: 'fuzzy' },
  { value: 'agent_number', label: 'Agent #', hint: 'exact' },
  { value: 'agent_id', label: 'Agent id', hint: 'exact' },
  { value: 'upline', label: 'Upline', hint: 'downline hierarchy' },
];

export const AGENT_RESEARCH_SOURCE_OPTIONS: { value: AgentResearchSource; label: string }[] = [
  { value: 'registry', label: 'Known agents (registry)' },
  { value: 'downline', label: 'Carrier downline' },
];
